import { useState, type FormEvent } from "react";
import { createDrop, addDropWhitelist, setDropState } from "../api/actions";
import { logActivity } from "../api/activityLog";
import { useWallet } from "../wallet/WalletContext";
import { backingTokens, contracts } from "../config";
import { showSuccess, showError } from "../components/Dialog";

function toChainTime(localDatetime: string): string {
  // <input type="datetime-local"> gives "2026-09-01T00:00", no timezone —
  // treat it as UTC (matches how the contract/chain interprets time_point_sec).
  return localDatetime.length === 16 ? `${localDatetime}:00` : localDatetime;
}

export function CreateDrop() {
  const { session, connect, connecting, account } = useWallet();
  const [busy, setBusy] = useState<string | null>(null);

  const [collectionName, setCollectionName] = useState("");
  const [schemaName, setSchemaName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [priceAmount, setPriceAmount] = useState("");
  const [priceSymbol, setPriceSymbol] = useState<"WAX" | "EOS">("WAX");
  const [backedDrop, setBackedDrop] = useState(false);
  const [backingAmount, setBackingAmount] = useState("");
  const [beneficiary, setBeneficiary] = useState("");
  const [maxPerWallet, setMaxPerWallet] = useState("0");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [whitelistEnabled, setWhitelistEnabled] = useState(false);

  const [wlDropId, setWlDropId] = useState("");
  const [wlAccounts, setWlAccounts] = useState("");
  const [wlMaxMints, setWlMaxMints] = useState("0");

  const [pauseDropId, setPauseDropId] = useState("");

  async function ensureSession() {
    if (session) return session;
    await connect();
    return undefined;
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    const s = await ensureSession();
    if (!s) return;
    setBusy("create");
    try {
      const precision = backingTokens[priceSymbol].precision;
      const priceQuantity = `${Number(priceAmount).toFixed(precision)} ${priceSymbol}`;
      const backingQuantity = backedDrop
        ? `${Number(backingAmount).toFixed(precision)} ${priceSymbol}`
        : `0.${"0".repeat(precision)} ${priceSymbol}`;

      await createDrop(s, {
        collectionName,
        schemaName,
        templateId: Number(templateId),
        price: { quantity: priceQuantity, contract: backingTokens[priceSymbol].contract },
        backing: { quantity: backingQuantity, contract: backingTokens[priceSymbol].contract },
        beneficiary: beneficiary || account || "",
        maxPerWallet: Number(maxPerWallet),
        startTime: toChainTime(startTime),
        endTime: toChainTime(endTime),
        whitelistEnabled,
      });
      logActivity({
        type: "drop_created",
        account: s.actor.toString(),
        collectionName,
        templateId: Number(templateId),
        amount: priceQuantity,
      });
      showSuccess(
        "Drop created — find its drop_id in the transaction trace, or by reading nftdrops' \"drops\" table " +
          "(sorted by drop_id, yours will be the newest), then use it below for whitelisting."
      );
    } catch (err) {
      showError(err, "Failed to create drop");
    } finally {
      setBusy(null);
    }
  }

  async function handleAddWhitelist(e: FormEvent) {
    e.preventDefault();
    const s = await ensureSession();
    if (!s) return;
    setBusy("whitelist");
    try {
      const accounts = wlAccounts
        .split(",")
        .map((a) => a.trim())
        .filter(Boolean);
      await addDropWhitelist(s, { dropId: wlDropId, accounts, maxMints: Number(wlMaxMints) });
      showSuccess(`Whitelisted ${accounts.length} account(s).`);
    } catch (err) {
      showError(err, "Failed to add whitelist");
    } finally {
      setBusy(null);
    }
  }

  async function handleTogglePause(enabled: boolean) {
    const s = await ensureSession();
    if (!s || !pauseDropId) return;
    setBusy(enabled ? "resume" : "pause");
    try {
      await setDropState(s, { dropId: pauseDropId, enabled });
      showSuccess(enabled ? "Drop resumed." : "Drop paused.");
    } catch (err) {
      showError(err, "Failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page page--narrow">
      <div className="page__header">
        <h1>Create a Drop</h1>
        <p className="page__subtitle">
          For collection authors (or accounts they've authorized). Works for any collection that's granted the
          right permissions — see the nftdrops README for the exact <code>addcolauth</code> step for plain
          drops, or registering a rate with <code>{contracts.escrow}</code> first for backed drops.
        </p>
      </div>

      <form className="mint-form" onSubmit={handleCreate}>
        <label>
          Collection name
          <input value={collectionName} onChange={(e) => setCollectionName(e.target.value)} required />
        </label>
        <label>
          Schema name
          <input value={schemaName} onChange={(e) => setSchemaName(e.target.value)} required />
        </label>
        <label>
          Template ID
          <input value={templateId} onChange={(e) => setTemplateId(e.target.value)} required />
        </label>

        <div className="mint-form__amount-row">
          <label>
            Price
            <input type="number" min="0" step="0.00000001" value={priceAmount} onChange={(e) => setPriceAmount(e.target.value)} required />
          </label>
          <label>
            Token
            <select value={priceSymbol} onChange={(e) => setPriceSymbol(e.target.value as "WAX" | "EOS")}>
              <option value="WAX">WAX</option>
              <option value="EOS">EOS</option>
            </select>
          </label>
        </div>

        <label>
          <input type="checkbox" checked={backedDrop} onChange={(e) => setBackedDrop(e.target.checked)} /> Backed
          drop (mints a claim-NFT via escrowbacker instead of an ordinary NFT)
        </label>

        {backedDrop && (
          <label>
            Backing amount (must exactly match escrowbacker's registered rate for this template; any
            price above this becomes your fee)
            <input
              type="number"
              min="0"
              step="0.00000001"
              value={backingAmount}
              onChange={(e) => setBackingAmount(e.target.value)}
              required={backedDrop}
            />
          </label>
        )}

        <label>
          Beneficiary (receives the full price for a plain drop, or price − backing for a backed drop; defaults
          to your account)
          <input value={beneficiary} onChange={(e) => setBeneficiary(e.target.value)} placeholder={account ?? ""} />
        </label>

        <label>
          Max mints per wallet (0 = unlimited)
          <input type="number" min="0" value={maxPerWallet} onChange={(e) => setMaxPerWallet(e.target.value)} />
        </label>

        <div className="mint-form__amount-row">
          <label>
            Start time
            <input type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
          </label>
          <label>
            End time
            <input type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
          </label>
        </div>

        <label>
          <input type="checkbox" checked={whitelistEnabled} onChange={(e) => setWhitelistEnabled(e.target.checked)} />{" "}
          Whitelist only
        </label>

        <button className="btn btn--primary" type="submit" disabled={busy === "create" || connecting}>
          {busy === "create" ? "Creating…" : "Create drop"}
        </button>
      </form>

      <form className="mint-form" onSubmit={handleAddWhitelist}>
        <h2>Add to whitelist</h2>
        <label>
          Drop ID
          <input value={wlDropId} onChange={(e) => setWlDropId(e.target.value)} required />
        </label>
        <label>
          Accounts (comma-separated)
          <input value={wlAccounts} onChange={(e) => setWlAccounts(e.target.value)} placeholder="alice, bob" required />
        </label>
        <label>
          Per-account mint cap override (0 = use the drop's default limit)
          <input type="number" min="0" value={wlMaxMints} onChange={(e) => setWlMaxMints(e.target.value)} />
        </label>
        <button className="btn btn--primary" type="submit" disabled={busy === "whitelist"}>
          {busy === "whitelist" ? "Adding…" : "Add to whitelist"}
        </button>
      </form>

      <form className="mint-form" onSubmit={(e) => e.preventDefault()}>
        <h2>Pause / resume a drop</h2>
        <label>
          Drop ID
          <input value={pauseDropId} onChange={(e) => setPauseDropId(e.target.value)} required />
        </label>
        <div className="mint-form__amount-row">
          <button className="btn btn--outline" type="button" onClick={() => handleTogglePause(false)} disabled={busy === "pause"}>
            {busy === "pause" ? "Working…" : "Pause"}
          </button>
          <button className="btn btn--primary" type="button" onClick={() => handleTogglePause(true)} disabled={busy === "resume"}>
            {busy === "resume" ? "Working…" : "Resume"}
          </button>
        </div>
      </form>
    </div>
  );
}
